<template>
  <div class="case-manager space-y-6 animate-[fadeIn_0.6s_ease-out]">
    <!-- 前台关联提示 -->
    <div class="bg-blue-50/80 backdrop-blur-sm border border-blue-100/60 rounded-2xl p-4 flex items-start gap-3 text-sm text-blue-700">
      <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <div>
        <p class="font-medium mb-1">前台关联</p>
        <p class="text-blue-600">这些案例是前台「专家详情页」和「话题详情页」的核心内容，用户通过案例了解专家的实战经验和解决方案</p>
      </div>
    </div>

    <!-- 搜索 + 操作栏 -->
    <div class="flex items-center justify-between gap-4">
      <div class="relative flex-1 max-w-md">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="搜索标题、摘要、标签或专家..."
          class="w-full px-4 py-2 pl-10 bg-white border border-slate-200 rounded-full text-sm focus:outline-none focus:border-[#F2C94C] focus:ring-2 focus:ring-[#F2C94C]/20 transition-all"
        />
        <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
      <div class="flex items-center gap-2">
        <button
          @click="downloadTemplate"
          class="flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 text-sm rounded-full hover:bg-slate-50 transition-colors"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          下载模板
        </button>
        <button
          @click="showBatchImport = true"
          class="flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 text-sm rounded-full hover:bg-slate-50 transition-colors"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          批量导入
        </button>
        <button
          v-if="hasPerm(PERMISSIONS.CREATE_CASE)"
          @click="handleAdd"
          class="flex items-center gap-1.5 px-4 py-2 bg-[#F2C94C] text-white text-sm font-medium rounded-full hover:bg-[#E5B73B] transition-colors shadow-sm"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          新增案例
        </button>
      </div>
    </div>

    <!-- 案例列表 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <el-table :data="filteredCases" stripe size="small" style="width: 100%">
        <el-table-column prop="id" label="ID" width="60" />
        <el-table-column label="标题" min-width="180">
          <template #default="{ row }">
            <span class="text-sm font-medium text-slate-900">{{ row.title }}</span>
          </template>
        </el-table-column>
        <el-table-column label="所属专家" width="100">
          <template #default="{ row }">
            <span class="text-sm text-slate-600">{{ row.expertName }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template #default="{ row }">
            <span
              :class="[
                'inline-flex items-center px-2 py-0.5 text-xs rounded font-medium',
                row.publishStatus === 'published'
                  ? 'bg-emerald-50 text-emerald-600'
                  : row.publishStatus === 'draft'
                    ? 'bg-slate-100 text-slate-500'
                    : 'bg-red-50 text-red-500'
              ]"
            >
              {{ statusLabel(row.publishStatus) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="标签" width="160">
          <template #default="{ row }">
            <div class="flex flex-wrap gap-1">
              <span
                v-for="tag in row.tags?.slice(0, 3)"
                :key="tag"
                class="inline-flex items-center px-1.5 py-0.5 bg-[#F2C94C]/10 text-[#F2C94C] text-xs rounded"
              >
                {{ tag }}
              </span>
              <span v-if="row.tags?.length > 3" class="text-xs text-slate-400">+{{ row.tags.length - 3 }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="关联话题" width="90" align="center">
          <template #default="{ row }">
            <span class="text-sm text-slate-600">{{ row.relatedTopicIds?.length || 0 }} 个</span>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="110">
          <template #default="{ row }">
            <span class="text-xs text-slate-500">{{ formatDate(row.createdAt) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="{ row }">
            <div class="flex items-center gap-2">
              <button
                @click="handlePreview(row)"
                class="text-slate-400 hover:text-slate-600 text-sm transition-colors"
              >
                预览
              </button>
              <button
                v-if="hasPerm(PERMISSIONS.EDIT_CASE)"
                @click="handleEdit(row)"
                class="text-[#F2C94C] hover:text-[#E5B73B] text-sm font-medium transition-colors"
              >
                编辑
              </button>
              <button
                v-if="hasPerm(PERMISSIONS.DELETE_CASE)"
                @click="handleDelete(row)"
                class="text-red-400 hover:text-red-500 text-sm font-medium transition-colors"
              >
                删除
              </button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 空状态 -->
      <div v-if="filteredCases.length === 0" class="text-center py-12">
        <svg class="w-12 h-12 text-slate-200 mx-auto mb-3" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
        <div class="text-slate-400 text-sm">暂无案例</div>
      </div>
    </div>

    <!-- 批量导入对话框 -->
    <el-dialog
      title="批量导入案例"
      :visible.sync="showBatchImport"
      width="600px"
      :close-on-click-modal="false"
    >
      <div class="space-y-4">
        <div class="bg-amber-50 border border-amber-100 rounded-lg p-3 text-sm text-amber-700">
          <p class="font-medium mb-1">导入说明</p>
          <ul class="list-disc list-inside space-y-0.5 text-amber-600">
            <li>请先下载模板，按模板格式填写数据</li>
            <li>支持 .xlsx 格式文件</li>
            <li>导入的案例默认状态为「草稿」</li>
          </ul>
        </div>

        <div class="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center hover:border-[#F2C94C] transition-colors">
          <input
            ref="fileInput"
            type="file"
            accept=".xlsx,.xls"
            class="hidden"
            @change="handleFileSelect"
          />
          <svg class="w-10 h-10 text-slate-300 mx-auto mb-3" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <p class="text-sm text-slate-500 mb-2">拖拽文件到此处，或</p>
          <button
            @click="$refs.fileInput.click()"
            class="px-4 py-1.5 bg-[#F2C94C] text-white text-sm rounded-full hover:bg-[#E5B73B] transition-colors"
          >
            选择文件
          </button>
        </div>

        <div v-if="importResult" class="bg-slate-50 rounded-lg p-3 text-sm">
          <p class="font-medium text-slate-700 mb-1">导入结果</p>
          <p class="text-emerald-600">成功导入 {{ importResult.success }} 条</p>
          <p v-if="importResult.failed > 0" class="text-red-500">失败 {{ importResult.failed }} 条</p>
          <div v-if="importResult.errors.length > 0" class="mt-2 space-y-1">
            <p v-for="(err, i) in importResult.errors.slice(0, 5)" :key="i" class="text-xs text-red-400">{{ err }}</p>
          </div>
        </div>
      </div>

      <span slot="footer">
        <button
          @click="showBatchImport = false"
          class="px-4 py-2 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50 transition-colors"
        >
          关闭
        </button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { logAction } from '../../services/audit'
import { EXPERT_CASES } from '../../data/cases'
import { EXPERTS } from '../../data'
import { TOPICS } from '../../data'
import { PERMISSIONS } from '../../data/adminConfig'
import * as XLSX from 'xlsx'

export default {
  name: 'CaseManager',

  data() {
    return {
      searchQuery: '',
      showBatchImport: false,
      importResult: null,
      PERMISSIONS,
    }
  },

  computed: {
    ...mapState(['cases']),
    ...mapGetters(['hasPermission']),

    caseList() {
      const dict = this.cases || { ...EXPERT_CASES }
      return Object.values(dict)
    },

    filteredCases() {
      let list = [...this.caseList]

      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase()
        list = list.filter(c =>
          c.title?.toLowerCase().includes(q) ||
          c.summary?.toLowerCase().includes(q) ||
          c.tags?.some(t => t.toLowerCase().includes(q)) ||
          c.expertName?.toLowerCase().includes(q)
        )
      }

      return list
    },
  },

  methods: {
    hasPerm(permission) {
      return this.hasPermission(permission)
    },

    statusLabel(status) {
      const labels = {
        draft: '草稿',
        published: '已发布',
        offline: '已下线',
      }
      return labels[status] || status
    },

    formatDate(timestamp) {
      if (!timestamp) return '-'
      const d = new Date(timestamp)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },

    handleAdd() {
      this.$router.push('/admin/cases/edit')
    },

    handleEdit(row) {
      this.$router.push(`/admin/cases/edit?id=${row.id}`)
    },

    handlePreview(row) {
      const url = `/expert/${row.expertId}/case/${row.id}`
      window.open(url, '_blank')
    },

    handleDelete(row) {
      this.$confirm(`确定删除案例「${row.title}」？此操作不可恢复。`, '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        const dict = { ...(this.cases || { ...EXPERT_CASES }) }
        delete dict[row.id]
        this.$store.commit('SET_CASES', dict)

        logAction({
          action: 'DELETE',
          targetType: 'case',
          targetId: row.id,
          reason: `删除案例: ${row.title}`,
          source: 'admin-ui',
          actor: { role: this.$store.state.userRole },
        })

        this.$message.success('删除成功')
      }).catch(() => {})
    },

    downloadTemplate() {
      const headers = ['标题', '所属专家ID', '摘要', '背景', '挑战/难度', '完整内容', '专家洞察', '标签（逗号分隔）', '视频URL', '音频URL', '关联话题ID（逗号分隔）']
      const example = ['核心员工离职预警与挽留实战', 'e1', '一句话描述案例...', '案例背景...', '挑战描述...', '完整案例内容（支持Markdown）...', '专家核心洞察...', '执行力,人才挽留', 'https://example.com/video.mp4', 'https://example.com/audio.mp3', '3,6']

      const ws = XLSX.utils.aoa_to_sheet([headers, example])
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '案例导入模板')
      XLSX.writeFile(wb, '案例导入模板.xlsx')
    },

    handleFileSelect(event) {
      const file = event.target.files[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
          const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 })

          if (rows.length < 2) {
            this.$message.error('文件为空或格式不正确')
            return
          }

          const headers = rows[0]
          const result = { success: 0, failed: 0, errors: [] }
          const newCases = {}

          for (let i = 1; i < rows.length; i++) {
            const row = rows[i]
            const title = row[0]?.toString().trim()
            const expertId = row[1]?.toString().trim()

            if (!title) {
              result.failed++
              result.errors.push(`第 ${i + 1} 行：标题不能为空`)
              continue
            }

            const expert = EXPERTS.find(e => e.id === expertId)
            if (!expert) {
              result.failed++
              result.errors.push(`第 ${i + 1} 行：专家ID「${expertId}」不存在`)
              continue
            }

            const caseId = `case-${Date.now()}-${i}`
            newCases[caseId] = {
              id: caseId,
              title,
              expertId,
              expertName: expert.name,
              summary: row[2]?.toString().trim() || '',
              background: row[3]?.toString().trim() || '',
              difficulty: row[4]?.toString().trim() || '',
              content: row[5]?.toString().trim() || '',
              expertInsight: row[6]?.toString().trim() || '',
              tags: row[7] ? row[7].toString().split(',').map(t => t.trim()).filter(Boolean) : [],
              videoUrl: row[8]?.toString().trim() || '',
              audioUrl: row[9]?.toString().trim() || '',
              relatedTopicIds: row[10] ? row[10].toString().split(',').map(t => t.trim()).filter(Boolean) : [],
              publishStatus: 'draft',
              createdAt: Date.now(),
            }
            result.success++
          }

          if (result.success > 0) {
            const dict = { ...(this.cases || { ...EXPERT_CASES }), ...newCases }
            this.$store.commit('SET_CASES', dict)

            logAction({
              action: 'CREATE',
              targetType: 'case',
              targetId: 'batch',
              reason: `批量导入案例: ${result.success} 条`,
              source: 'admin-ui',
              actor: { role: this.$store.state.userRole },
            })
          }

          this.importResult = result
          if (result.success > 0) {
            this.$message.success(`成功导入 ${result.success} 条案例`)
          }
          if (result.failed > 0) {
            this.$message.warning(`${result.failed} 条导入失败`)
          }
        } catch (err) {
          this.$message.error('文件解析失败：' + err.message)
        }
      }
      reader.readAsArrayBuffer(file)
      event.target.value = ''
    },
  },
}
</script>
